import MobileFriendly from '@mui/icons-material/MobileFriendly';
import Add from '@mui/icons-material/Add';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Downloading from '@mui/icons-material/Downloading';
import Home from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import * as spaUrls from 'utils/spaUrls';
import React from 'react';
import { Preview } from '@mui/icons-material';
import {
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableRow,
  styled,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

interface AddNewPatientSuccessProps {
  onAddAnotherPatientClick: VoidFunction;
  patientFormDemographic: boolean;
  addedPatientWithoutInvite: boolean;
  isBulkUpload?: boolean;
}

const StyledTableCell = styled(TableCell)<TableCellProps>(() => ({
  borderBottom: 'none',
  padding: '8px',
}));

export default function AddNewPatientSuccess({
  onAddAnotherPatientClick,
  patientFormDemographic,
  addedPatientWithoutInvite,
  isBulkUpload = false,
}: AddNewPatientSuccessProps) {
  const demographicSuccessForm = () => (
    <Table sx={{ maxWidth: 470, mx: 'auto', my: 2 }}>
      <TableBody>
        <TableRow>
          <StyledTableCell>
            <Downloading color='primary' fontSize='large' />
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant='body1'>
              We&apos;ll start pulling the patient&apos;s health history now
            </Typography>
          </StyledTableCell>
        </TableRow>
        {!addedPatientWithoutInvite && (
          <TableRow>
            <StyledTableCell>
              <MobileFriendly color='primary' fontSize='large' />
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant='body1'>
                An invite has been sent to the patient to download the Headlamp
                mobile app and create their account
              </Typography>
            </StyledTableCell>
          </TableRow>
        )}
        <TableRow>
          <StyledTableCell>
            <Preview color='primary' fontSize='large' />
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant='body1'>
              When ready, you can view their information from the home page
            </Typography>
          </StyledTableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  const bulkUploadSuccessForm = () => (
    <Table sx={{ maxWidth: 470, mx: 'auto', my: 2 }}>
      <TableBody>
        <TableRow>
          <StyledTableCell>
            {!isBulkUpload ? (
              <CheckCircle color='success' fontSize='large' />
            ) : (
              <Downloading color='primary' fontSize='large' />
            )}
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant='body1'>The file will be processed within 48 hours</Typography>
          </StyledTableCell>
        </TableRow>
        <TableRow>
          <StyledTableCell>
            <MobileFriendly color='primary' fontSize='large' />
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant='body1'>
              An invite will be sent to each patient to download
              the Headlamp mobile app and create their account
            </Typography>
          </StyledTableCell>
        </TableRow>
        <TableRow>
          <StyledTableCell>
            <Preview color='primary' fontSize='large' />
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant='body1'>
              When ready, you can view their information from the home page
            </Typography>
          </StyledTableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  const invitePatientSuccessForm = () => (
    <Table sx={{ maxWidth: 470, mx: 'auto', my: 2 }}>
      <TableBody>
        <TableRow>
          <StyledTableCell>
            <MobileFriendly color='primary' fontSize='large' />
          </StyledTableCell>
          <StyledTableCell>
            <Box>
              <Typography variant='body1'>
                Patient(s) will be invited to:
              </Typography>
              <List disablePadding sx={{ listStyleType: 'disc', ml: 2 }}>
                <ListItem sx={{ display: 'list-item', p: 0 }}>
                  <Typography variant='body1'>download the Headlamp mobile app</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: 0 }}>
                  <Typography variant='body1'>consent to pulling health records</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: 0 }}>
                  <Typography variant='body1'>create their Headlamp account</Typography>
                </ListItem>
              </List>
            </Box>
          </StyledTableCell>
        </TableRow>
        <TableRow>
          <StyledTableCell>
            <Preview color='primary' fontSize='large' />
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant='body1'>
              Once this is completed, you can view their health history from the home page
            </Typography>
          </StyledTableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  let successForm;
  if (isBulkUpload) {
    successForm = bulkUploadSuccessForm();
  }
  else if (patientFormDemographic) {
    successForm = demographicSuccessForm();
  }
  else {
    successForm = invitePatientSuccessForm();
  }

  return (
    <Grid container spacing={2} textAlign='center'>
      <Grid item xs={12}>
        <Box display='flex' flexDirection='row' justifyContent='center'>
          <CheckCircle color='success' fontSize='medium' />
          <Typography variant='h5'>&nbsp;Success</Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='h5'>&nbsp;What&apos;s Next?</Typography>
      </Grid>
      {successForm}
      <Grid item xs={12}>
        <Button
          startIcon={<Add />}
          onClick={() => {
            trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_ADD_ANOTHER_BUTTON_CLICKED);
            onAddAnotherPatientClick();
          }}
          variant='contained'
          color='primary'
          sx={{ textTransform: 'uppercase', mb: 1 }}
        >
          Add Another Patient
        </Button>
      </Grid>
      {!isBulkUpload && (
        <Grid item xs={12}>
          <Button
            component={Link}
            to={spaUrls.mainNav.dashboard}
            startIcon={<Home />}
            variant='outlined'
            color='primary'
            sx={{ textTransform: 'uppercase', mb: 1 }}
            onClick={() => trackMixpanelEvent(MixpanelEventName
              .ADD_PATIENT_RETURN_HOME_BUTTON_CLICKED)}
          >
            Return Home
          </Button>
        </Grid>
      )}
      {isBulkUpload && (
        <Grid item xs={12}>
          <Button
            startIcon={<HistoryIcon />}
            variant='outlined'
            color='primary'
            sx={{ textTransform: 'uppercase' }}
            onClick={() => {
              trackMixpanelEvent(MixpanelEventName
                .BULK_UPLOAD_PATIENTS_VIEW_HISTORY_BUTTON_CLICKED);
              sessionStorage.setItem('openBulkUploadHistory', 'true');
              window.location.reload();
            }}
          >
            view bulk upload history
          </Button>
        </Grid>
      )}
    </Grid>
  );
}
